import { EditOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { Button, Divider, Select, Switch, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { DataCard } from "~/components/dataCards/dataCard.jsx";
import { MemberModal } from "./memberModal.jsx";
import { $t } from "~/i18n.js";
import { HelpBubble } from "~/components/help/helpBubble.jsx";
import { HelpLine } from "~/components/help/helpLine.jsx";

const roles = {
  arc_technician: { label: "ARC technicus", color: "gold" },
  technician: { label: "Technicus", color: "green" },
  admin: { label: "Admin", color: "orange" },
  manager: { label: "Projectmanager", color: "cyan" },
  member: { label: "Projectlid", color: "default" },
};

export const MemberRow = ({ member, setEditMember }) => (
  <div
    className="row"
    style={{
      justifyContent: "space-between",
      gap: 12,
      paddingBottom: 3,
      marginBottom: 6,
      borderBottom: "1px solid #f0f0f0",
      opacity: member.action === "delete" ? 0.3 : 1,
    }}
  >
    <p>{member.user?.name}</p>

    <Tag color={roles[member.role].color}>{roles[member.role].label}</Tag>

    <div className="row" style={{ marginLeft: "auto", gap: 6 }}>
      {member.action && (
        <>
          {member.action === "delete" ? (
            <Tag icon={<StopOutlined />} color="warning" />
          ) : (
            <Tag color="green">{member.action}</Tag>
          )}
        </>
      )}

      {setEditMember && (
        <Button
          shape="circle"
          icon={<EditOutlined />}
          size="small"
          onClick={() =>
            member && setEditMember({ ...member, action: "wijzig" })
          }
        />
      )}
    </div>
  </div>
);

export const CardMembers = ({
  saveData,
  readOnly,
  members,
  kist,
  currentMaterial,
}) => {
  const { profile } = useSelector((state) => state.persist);
  const [loading, setLoading] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const itemsForSelect = members.map((member, idx) => (
    <Select.Option key={idx} value={member.user?.id}>
      {member.user?.name}
    </Select.Option>
  ));

  const [visible_for_technicians, setVisibleForAll] = useState(false);
  const [defaultPilotId, setDefaultPilotId] = useState(null);
  const [defaultMeldingAssignedId, setDefaultMeldingAssignedId] =
    useState(null);
  const [enableCheckin, setEnableCheckin] = useState(false);

  useEffect(() => {
    setVisibleForAll(kist?.visible_for_technicians);
    setDefaultPilotId(kist?.default_pilot_id);
  }, [kist]);

  useEffect(() => {
    setEnableCheckin(currentMaterial.enable_checkin);
    setDefaultMeldingAssignedId(currentMaterial?.default_melding_assigned_id);
  }, [currentMaterial]);

  const isClub =
    !kist || kist?.category === "sleep" || kist?.category === "club";

  const [memberList, setMemberList] = useState([]);
  useEffect(() => {
    setMemberList(members);
  }, [members]);

  const saveMember = async ({ member }) => {
    setLoading(true);
    const res = await saveData({members: [member]});
    setLoading(false);
    setEditMember(null);
    return res;
  };

  const triggerSave = async () => {
    setLoading(true);
    const res = await saveData({
      kist: {
        visible_for_technicians,
        default_pilot_id: defaultPilotId,
      },
      enable_checkin: enableCheckin,
      default_melding_assigned_id: defaultMeldingAssignedId,
    });
    setLoading(false);
    return res;
  };

  return (
    <DataCard
      title="Team"
      readOnly={readOnly}
      loading={loading}
      onSave={triggerSave}
    >
      <HelpLine style={{ marginBottom: 12 }}>
        {$t(
          "Hieronder kan je leden toevoegen aan het team, en de algemene toegang bepalen. In bijzonder geldt:"
        )}{" "}
        {isClub ? (
          <p>
            <span className="bold">{$t("Clubmateriaal")}</span>:{" "}
            {$t(
              "voor alle leden in te zien en alle technici mogen wijzigingen doen."
            )}
          </p>
        ) : (
          <p>
            <span className="bold">{$t("Prive materiaal")}</span>:{" "}
            {$t(
              "alleen toegevoegde leden (en admin gebruikers) kunnen het materiaal inzien."
            )}
          </p>
        )}
      </HelpLine>

      <MemberModal
        loading={loading}
        currentProfile={profile}
        isClub={isClub}
        saveMember={saveMember}
        member={editMember}
        closeModal={() => setEditMember(null)}
      />
      {!readOnly && (
        <Button
          onClick={() =>
            setEditMember({
              role: "member",
              action: "nieuw",
              user: { group_names: [] },
            })
          }
          size="small"
          icon={<PlusOutlined />}
        >
          {$t("Voeg toe")}
        </Button>
      )}
      <div
        style={{ marginTop: 12, maxHeight: 275, overflowY: "auto", width: 320 }}
      >
        {memberList.map((member, idx) => (
          <MemberRow
            key={idx}
            member={member}
            setEditMember={
              !readOnly && profile.id !== member.user?.id && setEditMember
            }
          />
        ))}

        {memberList?.length === 0 && (
          <p>{$t("Geen gebruikers toegevoegd aan team")}</p>
        )}
      </div>

      <p className="subtitle" style={{ marginTop: 12 }}>
        {$t("Team settings")}
      </p>

      {!isClub && kist && (
        <div className="row" style={{ margin: "12px 0", gap: 12 }}>
          <p className="label">
            {$t("Toegang voor alle technici")}
            <HelpBubble
              content={$t(
                "Indien ja, dan is de kist zichtbaar voor alle technici in de club."
              )}
            />
          </p>
          <Switch
            disabled={readOnly}
            checkedChildren="Ja"
            unCheckedChildren="Nee"
            checked={visible_for_technicians}
            onChange={(newValue) => setVisibleForAll(newValue)}
          />
        </div>
      )}

      {!isClub && kist && (
        <div
          className="row"
          style={{ margin: "12px 0", gap: 12, alignItems: "center" }}
        >
          <p>
            {$t("Piloot")}{" "}
            <HelpBubble
              content={$t(
                "Indien een vlucht gestart is zal dit lid automatisch aangemeld worden als gezagvoerder"
              )}
            />
            :
          </p>
          <Select
            disabled={readOnly}
            style={{ width: 200 }}
            placeholder={$t("Selecteer lid")}
            value={defaultPilotId}
            onChange={(newValue) => setDefaultPilotId(newValue)}
          >
            <Select.Option value={null}>{$t("Geen")}</Select.Option>
            {itemsForSelect}
          </Select>
        </div>
      )}

      <div
        className="row"
        style={{ margin: "12px 0", gap: 12, alignItems: "center" }}
      >
        <p>
          {$t("Toewijzing meldingen")}{" "}
          <HelpBubble
            content={$t(
              "Gebruiker aan wie initieel klachten worden toegekend, dit kan later worden aangepast"
            )}
          />
          :
        </p>
        <Select
          disabled={readOnly}
          style={{ width: 200 }}
          placeholder={$t("Selecteer lid")}
          value={defaultMeldingAssignedId}
          onChange={(newValue) => setDefaultMeldingAssignedId(newValue)}
        >
          <Select.Option value={null}>{$t("Geen")}</Select.Option>
          {itemsForSelect}
        </Select>
      </div>

      <div className="row" style={{ margin: "12px 0", gap: 12 }}>
        <p>
          {$t("Check-in mogelijk")}{" "}
          <HelpBubble
            content={$t(
              "Geeft leden de mogelijkheid om in te checken en vlucht 'klaar' te zetten in de grid"
            )}
          />
          :
        </p>
        <Switch
          disabled={readOnly}
          checkedChildren="Ja"
          unCheckedChildren="Nee"
          checked={enableCheckin}
          onChange={(newValue) => setEnableCheckin(newValue)}
        />
      </div>
    </DataCard>
  );
};
